import React, { useContext, useLayoutEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { Transition, TransitionGroup } from 'react-transition-group'
import { gsap, ScrollSmoother } from 'gsap-wrapper'
import LayoutProvider, { LayoutContext } from 'styles/layout-provider'

import 'styles/fonts.css'
import GlobalStyle from 'styles/global-styles'
import { LayoutWrapper, PageWrapper, ContentWrapper } from './index.styled'
import useWindowSize from 'styles/window-size'

const LayoutContent = ({ children, location, className, reinitializeObserver }) => {
  const { scrollSmoother } = useContext(LayoutContext)
  const { width } = useWindowSize()

  const handleEntered = () => {
    setTimeout(() => {
      reinitializeObserver()
    }, 10)

    if (width < 768) {
      if (scrollSmoother.current) {
        scrollSmoother.current.scrollTop(0, false)
      } else {
        window.scrollTo(0, 0)
      }
    }
  }

  return (
    <div className={className}>
      <TransitionGroup component={null}>
        <Transition
          key={location.pathname}
          timeout={{ enter: 100, exit: 0 }}
          onEntered={handleEntered}
        >
          {(status) => (
            <div className={`page ${status}`}>
              <LayoutWrapper>{children}</LayoutWrapper>
            </div>
          )}
        </Transition>
      </TransitionGroup>
    </div>
  )
}

const LayoutGsapWrapper = ({ children, location }) => {
  const { scrollSmoother } = useContext(LayoutContext)
  const refWrapper = useRef(null)
  const observerRef = useRef(null)

  useLayoutEffect(() => {
    const gsapContext = gsap.context(() => {
      scrollSmoother.current = ScrollSmoother.create({
        content: '.content',
        wrapper: '.wrapper',
        smooth: 2
      })
    }, [refWrapper, scrollSmoother])

    return () => gsapContext.revert()
  }, [children, scrollSmoother])

  const initializeObserver = useCallback(() => {
    if (observerRef.current) {
      observerRef.current.disconnect()
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            observerRef.current.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 }
    )

    document.querySelectorAll('.fadeIn').forEach((el) => {
      el.classList.remove('visible')
      observerRef.current.observe(el)
    })
  }, [])

  React.useEffect(() => {
    initializeObserver()

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect()
      }
    }
  }, [initializeObserver])

  return (
    <PageWrapper>
      <ContentWrapper className='wrapper' ref={refWrapper}>
        <LayoutContent
          location={location}
          className='content'
          reinitializeObserver={initializeObserver}
        >
          {children}
        </LayoutContent>
      </ContentWrapper>
    </PageWrapper>
  )
}

const Layout = ({ children, location }) => {
  return (
    <LayoutProvider location={location}>
      <GlobalStyle />
      <LayoutGsapWrapper location={location}>
        {children}
      </LayoutGsapWrapper>
    </LayoutProvider>
  )
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  location: PropTypes.object.isRequired
}

export default Layout
